import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';

function CategoryTransactionsModal({ category, month, onClose, onEditTransaction }) {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTransactions();
  }, [category, month]);

  async function loadTransactions() {
    try {
      setLoading(true);
      const data = await api.getTransactions(month);
      
      // Filter transactions for this category (including splits)
      const filtered = data.filter(t => 
        t.category_id === category.id || t.split_category_id === category.id
      );
      
      // Sort by date descending
      filtered.sort((a, b) => new Date(b.date) - new Date(a.date));
      
      setTransactions(filtered);
    } catch (error) {
      console.error('Failed to load transactions:', error);
    } finally {
      setLoading(false);
    }
  }

  function formatDate(dateString) {
    // Fix timezone issue by parsing the date string directly
    const [year, month, day] = dateString.split('-');
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString();
  }

  function calculateTotals() {
    let totalAmount = 0;
    let totalSplitOut = 0;
    
    transactions.forEach(t => {
      if (t.category_id === category.id) {
        // This category is the main category
        const amountAfterSplit = t.amount - (t.split_amount || 0);
        totalAmount += amountAfterSplit;
        totalSplitOut += (t.split_amount || 0);
      } else if (t.split_category_id === category.id) {
        // This category received a split
        totalAmount += (t.split_amount || 0);
      }
    });
    
    return { totalAmount, totalSplitOut };
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2>{category.name} - Transactions</h2>
            <small className="modal-subtitle">{month}</small>
          </div>
          <button onClick={onClose} className="modal-close">✕</button>
        </div>

        <div className="modal-body">
          {loading ? (
            <div className="loading">Loading transactions...</div>
          ) : transactions.length === 0 ? (
            <p className="empty-state">No transactions for this category in {month}</p>
          ) : (
            <>
              <div className="category-transactions-summary">
                <div className="summary-item">
                  <span className="summary-label">Total Transactions:</span>
                  <span className="summary-value">{transactions.length}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Total Amount:</span>
                  <span className={`summary-value ${category.type}`}>
                    ${calculateTotals().totalAmount.toFixed(2)}
                  </span>
                </div>
                {calculateTotals().totalSplitOut > 0 && (
                  <div className="summary-item">
                    <span className="summary-label">Total Split Out:</span>
                    <span className={`summary-value ${category.type}`}>
                      ${calculateTotals().totalSplitOut.toFixed(2)}
                    </span>
                  </div>
                )}
              </div>

              <div className="transactions-list compact">
                {transactions.map(transaction => {
                  const isMainCategory = transaction.category_id === category.id;
                  let displayAmount;
                  
                  if (isMainCategory) {
                    // Main category: show amount after split
                    displayAmount = transaction.amount - (transaction.split_amount || 0);
                  } else {
                    // Split category: show split amount received
                    displayAmount = transaction.split_amount;
                  }
                  
                  return (
                    <div 
                      key={transaction.id} 
                      className="transaction-item compact"
                      onClick={() => {
                        onClose();
                        onEditTransaction(transaction);
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="transaction-main">
                        <div className="transaction-date">
                          {formatDate(transaction.date)}
                        </div>
                        <div className="transaction-info">
                          <div className="transaction-merchant">{transaction.merchant}</div>
                          {!isMainCategory && (
                            <div className="transaction-category">
                              Split from {transaction.category_name}
                            </div>
                          )}
                          {isMainCategory && transaction.split_category_id && (
                            <div className="transaction-category">
                              <span className="split-indicator">
                                → {transaction.split_category_name} (${transaction.split_amount?.toFixed(2)})
                              </span>
                            </div>
                          )}
                          {transaction.description && (
                            <div className="transaction-description">{transaction.description}</div>
                          )}
                        </div>
                        <div className={`transaction-amount ${transaction.type} ${displayAmount < 0 ? 'negative' : ''}`}>
                          {displayAmount < 0 
                            ? (transaction.type === 'income' ? '-' : '↩ ')
                            : (transaction.type === 'income' ? '+' : '-')}
                          ${Math.abs(displayAmount).toFixed(2)}
                        </div>
                      </div>
                      {transaction.verified && (
                        <div className="transaction-badge verified">✓</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="btn btn-secondary">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default CategoryTransactionsModal;
